import { useMemo } from 'react'
import useAuth from 'app/hooks/useAuth'
import {
    canAccessClient,
    canLoginAsClientSa,
    canReadSaSection,
    canWriteSaSection,
    clientIdFromPath,
    getSaSectionAccess,
    hasSaSectionAccess,
    isMasterSa,
    isSaClientUnrestricted,
    minLevelForPath,
    resolveAllowedClientIds,
    resolveSaSectionAccess,
    sectionForPath,
} from 'app/auth/saAccess'

/**
 * Hook for Super Admin section ACL checks in the admin UI.
 */
export default function useSaAccess() {
    const { user } = useAuth()

    const sectionAccess = useMemo(
        () => resolveSaSectionAccess(user),
        [user]
    )

    const allowedClientIds = useMemo(
        () => resolveAllowedClientIds(user),
        [user]
    )

    const isSa = user?.role === 'SA'
    const master = isMasterSa(user)

    return useMemo(
        () => ({
            isSa,
            isMaster: master,
            sectionAccess,
            allowedClientIds,
            isClientUnrestricted: isSaClientUnrestricted(user),
            getLevel: (sectionKey) => getSaSectionAccess(user, sectionKey),
            canRead: (sectionKey) =>
                !isSa || canReadSaSection(user, sectionKey),
            canWrite: (sectionKey) =>
                !isSa || canWriteSaSection(user, sectionKey),
            canLoginAsClient: !isSa || canLoginAsClientSa(user),
            hasAccess: (sectionKey, minLevel = 'read') =>
                !isSa || hasSaSectionAccess(user, sectionKey, minLevel),
            canAccessClient: (clientId) =>
                !isSa || canAccessClient(user, clientId),
            canAccessPath: (pathname) => {
                if (!isSa) return true
                const sectionKey = sectionForPath(pathname)
                if (!sectionKey) return true
                const needed = minLevelForPath(pathname)
                if (!hasSaSectionAccess(user, sectionKey, needed)) return false

                if (sectionKey === 'clients') {
                    const clientId = clientIdFromPath(pathname)
                    // /pages/new-client/new has no id — allowed for write
                    if (clientId && !canAccessClient(user, clientId)) {
                        return false
                    }
                }
                return true
            },
        }),
        [user, isSa, master, sectionAccess, allowedClientIds]
    )
}
